// React core and hooks
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// Redux
import { selectPatientId } from "../../../../../../redux/features/patientState/patientStateSlice";
import {
  setActiveMenu,
  setActivePage,
} from "../../../../../../redux/features/applicationState/applicationStateSlice";

// API configuration
import useAxios from "../../../../../../hooks/useAxios";
import multipartFormatAuth from "../../../../../../apis/multipartFormatAuth";
import jsonFormatAuth from "../../../../../../apis/jsonFormatAuth";

// API helpers
import {
  processComparisonSaveFormData,
  processComparisonItems,
  sanitizeComparisonEntryDataResponse,
} from "../../../../../../helpers/apiUtils/comparisonUtils";
import { manageTagsSentResponse } from "../../../../../../helpers/entryUtils/manageTagsSentResponse";

// Custom components
import ImageViewModal from "../../../../../../components/ImageViewModal/ImageViewModal";
import ComparisonModal from "../../../../../../components/ComparisonModal/ComparisonModal";
import EntryScreenTemplate from "../../../../../../components/EntryScreenTemplate/EntryScreenTemplate";
import ComparisonMenu from "../../../../../../components/ComparisonMenu/ComparisonMenu";
import ImageViewer from "../../../../../../components/ImageViewer/ImageViewer";
import ComparisonCard from "../../../../../../components/ComparisonCard/ComparisonCard";
import NewPairButton from "../../../../../../components/NewPairButton/NewPairButton";
import EntryBrowserModal from "../../../../../../components/EntryBrowserModal/EntryBrowserModal";
import EntryBrowser from "../../../../../../components/EntryBrowser/EntryBrowser";

// Standard objects
import { baseComparisonItem } from "../../../../../../helpers/baseObjects/baseComparisonItem";

// Other utilities
import toastManager from "../../../../../../helpers/toastManager";
import cloneDeep from "lodash.clonedeep";
import { processRapidEntrySave } from "../../../../../../helpers/entryUtils/processRapidEntrySave";
import { handleRapidEntrySave } from "../../../../../../helpers/rapidPatientHandler";

function ComparisonEntryScreen(props) {
  const {
    rapidEntry = false,

    rapidPatientUniqueId,
    setRapidPatientUniqueId,

    rapidPatientFullName,
    setRapidPatientFullName,

    selectedExistingPatientId,
    setSelectedExistingPatientId,
    rapidEntryAction,
    setRapidEntryAction,
    // Field warnings.
    rapidPatientUniqueIdWarning,
    setRapidPatientUniqueIdWarning,
    rapidPatientFullNameWarning,
    // setRapidPatientFullNameWarning,
  } = props;

  // Navigation and application state
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const patientId = useSelector(selectPatientId);

  // API related
  const { response, error, axiosFetch } = useAxios();

  const apiUrl = `entries/comparison_entry/${id}/`;

  // Entry data and related state
  const [entrySavedStatus, setEntrySavedStatus] = useState(false);
  const [entryData, setEntryData] = useState({});

  // Image items-related state
  const pairLimit = 10;
  const [comparisonItems, setComparisonItems] = useState([
    cloneDeep(baseComparisonItem),
  ]);
  const [initialComparisonItems, setInitialComparisonItems] = useState([
    cloneDeep(baseComparisonItem),
  ]);

  // AppointmentDate-related state
  const [appointmentDate, setAppointmentDate] = useState("");
  const [initialAppointmentDate, setInitialAppointmentDate] = useState("");

  // Note-related state
  const [notes, setNotes] = useState("");
  const [initialNotes, setInitialNotes] = useState("");

  // Summary-related state
  const [summary, setSummary] = useState("");
  const [initialSummary, setInitialSummary] = useState("");

  // Tag-related state
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tags, setTags] = useState([]);
  const [initialTags, setInitialTags] = useState([]);

  // Image in image view modal
  const [modalImage, setModalImage] = useState(null);

  // Comparison modal
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [currentMenu, setCurrentMenu] = useState("split");
  const [touchControls, setTouchControls] = useState({
    split: false,
    opacity: false,
  });

  // Previous entires modal browser
  const [entryBrowserModalOpen, setEntryBrowserModalOpen] = useState(false);
  const [currentComparisonIndex, setCurrentComparisonIndex] = useState(0);
  const [comparisonItemLocation, setComparisonItemLocation] = useState({
    index: 0,
    fileKey: "fileOne",
  });

  // FUNCTIONS START
  useEffect(() => {
    // Obtaining data from API on initial page load
    getEntryData();
  }, []);

  // * Primary entry functions
  const getEntryData = () => {
    axiosFetch({
      axiosInstance: multipartFormatAuth(),
      method: "GET",
      url: apiUrl,
      sendSignal: false,
      requestConfig: {},
    });
  };

  const saveEntry = () => {
    const formData = new FormData();

    formData.append("appointment_date", appointmentDate);
    formData.append("notes", notes);
    formData.append("summary", summary);
    formData.append("classification_tags", JSON.stringify(tags));

    const isProcessingSuccessful = processComparisonSaveFormData({
      formData: formData,
      comparisonItems: comparisonItems,
    });

    if (!isProcessingSuccessful) {
      return;
    }

    const isProcessRapidEntrySaveSuccessful = processRapidEntrySave({
      formData,
      rapidEntry,
      rapidEntryAction,
      rapidPatientUniqueId,
      rapidPatientFullName,
      setRapidPatientUniqueIdWarning,
      selectedExistingPatientId,
    });
    if (!isProcessRapidEntrySaveSuccessful) {
      return;
    }

    axiosFetch({
      axiosInstance: multipartFormatAuth(),
      method: "POST",
      url: apiUrl,
      sendSignal: false,
      requestConfig: formData,
    }).catch((error) => {
      // Handle errors here
      toastManager(
        "error",
        "Error",
        "There was an error sending the entry data. Please try again."
      );
    });
  };

  const deleteEntry = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "DELETE",
      url: apiUrl,
      sendSignal: false,
      requestConfig: {
        // Attach delete_confirmation and if this is a rapid_entry
        data: { delete_confirmation: true, is_rapid_entry: rapidEntry },
      },
    });
  };

  // * Secondary entry functions
  const hasDataChanged = () => {
    return (
      JSON.stringify(initialComparisonItems) !==
        JSON.stringify(comparisonItems) ||
      appointmentDate !== initialAppointmentDate ||
      notes !== initialNotes ||
      summary !== initialSummary ||
      JSON.stringify(tags) !== JSON.stringify(initialTags)
    );
  };

  const handleSuccessfulSave = () => {
    toastManager(
      "success",
      "Entry Saved Successfully!",
      "All entry photos and data have been saved to the server."
    );
    setEntrySavedStatus(true);

    // Update values to detect changes
    setInitialTags([...tags]);
    setInitialAppointmentDate(appointmentDate);
    setInitialSummary(summary);
    setInitialNotes(notes);
    setInitialComparisonItems(cloneDeep(comparisonItems));
  };

  const handlePageLeave = () => {
    const leaveCondition = patientId || !rapidEntry;
    const activeLink = leaveCondition
      ? `/entries/comparison_menu/${patientId}`
      : "/";
    navigate(activeLink);
    dispatch(setActiveMenu(leaveCondition ? "patients" : "home"));
    dispatch(setActivePage(activeLink));
  };

  const handleMenuRedirect = () => {
    const dataChanged = hasDataChanged();

    let emptyPairs = false;
    let halfEmptyPairs = false;
    let fullPairs = false;

    for (const { fileOne, fileTwo } of comparisonItems) {
      const fileOneEmpty = fileOne.length === 0;
      const fileTwoEmpty = fileTwo.length === 0;

      if (fileOneEmpty && fileTwoEmpty) {
        emptyPairs = true;
      } else if (!fileOneEmpty && !fileTwoEmpty) {
        fullPairs = true;
      } else {
        halfEmptyPairs = true;
      }
    }

    if (emptyPairs && !dataChanged) {
      deleteEntry();
    } else if (!entrySavedStatus && dataChanged && fullPairs) {
      // Entry contains full pairs, is changed, and unsaved. Prompt to try and save
      toastManager(
        "warning",
        "Save Warning",
        "Entry has unsaved changes. Save entry"
      );
    } else if (!entrySavedStatus && (emptyPairs || halfEmptyPairs)) {
      // Entry contains some empty pairs / half pairs, and is unsaved. Prompt to try and save
      toastManager(
        "warning",
        "Warning",
        "Entry has unsaved changes and is missing images. Add images to pairs, or remove empty pairs and save entry."
      );
    } else if (!dataChanged || entrySavedStatus) {
      // Entry is either unchanged or entry is saved. Redirect to menu
      handlePageLeave();
    }
  };

  const getTagSuggestions = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "GET",
      url: "tags/autosuggestions/",
      sendSignal: false,
      requestConfig: {},
    });
  };
  // FUNCTIONS END

  // API START
  // RESPONSE Detection
  useEffect(() => {
    if (response) {
      // console.log("Response:");
      // console.log(response);

      const actionCode = response?.action_code;

      // Entry data received
      if (actionCode === "entry_sent") {
        // Request for tag suggestions
        getTagSuggestions();

        const entryDataResponse = response?.payload?.entry_data;

        if (entryDataResponse) {
          const sanitizedEntryDataResponse =
            sanitizeComparisonEntryDataResponse({ entryDataResponse });
          setEntryData(sanitizedEntryDataResponse);

          const { processedComparisonItems } = processComparisonItems({
            sanitizedEntryDataResponse,
          });

          setAppointmentDate(sanitizedEntryDataResponse.appointment_date);
          setInitialAppointmentDate(
            sanitizedEntryDataResponse.appointment_date
          );

          // setTags([...sanitizedEntryDataResponse.tags]);
          // setInitialTags([...sanitizedEntryDataResponse.tags]);

          setTags([...sanitizedEntryDataResponse.classification_tags]);
          setInitialTags([...sanitizedEntryDataResponse.classification_tags]);

          setSummary(sanitizedEntryDataResponse.summary);
          setInitialSummary(sanitizedEntryDataResponse.summary);

          setNotes(sanitizedEntryDataResponse.notes);
          setInitialNotes(sanitizedEntryDataResponse.notes);

          if (processedComparisonItems.length !== 0) {
            setComparisonItems([...processedComparisonItems]);
            setInitialComparisonItems(cloneDeep(processedComparisonItems));
          }
        }
      }

      // Entry successfully saved
      if (actionCode === "entry_saved") {
        handleSuccessfulSave();
      }

      if (
        actionCode === "rapid_entry_saved_to_new" ||
        actionCode === "rapid_entry_saved_to_existing"
      ) {
        handleRapidEntrySave({
          response,
          pageLink: `/entries/comparison_entry/${id}`,
          activePage: `/entries/comparison_menu/`,
          navigate,
          entryType: "photo",
        });
      }

      // Entry successfully deleted
      if (actionCode === "entry_deleted") {
        toastManager(
          "success",
          "Entry Deleted Successfully!",
          "All entry photos and data have been removed from the server."
        );
        handlePageLeave();
      }

      // Tag suggestions received
      if (actionCode === "tags_sent") {
        manageTagsSentResponse({ response, setTagSuggestions });
      }
    }
  }, [response]);

  // ERROR Detection
  useEffect(() => {
    if (error) {
      // console.log("Error:");
      // console.log(error);

      const actionCode = error?.action_code;

      // Entry does not exist.
      if (actionCode === "entry_not_exist" || error?.pk) {
        toastManager(
          "error",
          "Entry does not exist!",
          "Entry not found or not owned by the user."
        );
        handlePageLeave();
      }

      if (error?.rapid_patient_unique_id?.action_code === "unique_id_taken") {
        toastManager(
          "warning",
          "Patient ID taken!",
          "This Unique Patient ID is already assigned to another patient. Use a different one."
        );
        setRapidPatientUniqueIdWarning(true);
      }
    }
  }, [error]);
  // API END

  return (
    <EntryScreenTemplate
      entryTitle={"Comparison Entry"}
      // backLink={`/entries/comparison_menu/${patientId}`}
      handleMenuRedirect={handleMenuRedirect}
      appointmentDate={appointmentDate}
      setAppointmentDate={setAppointmentDate}
      suggestionList={tagSuggestions}
      tags={tags}
      setTags={setTags}
      notes={notes}
      setNotes={setNotes}
      summary={summary}
      setSummary={setSummary}
      saveEntryFunction={saveEntry}
      deleteEntryFunction={deleteEntry}
      entryChanged={hasDataChanged()}
      entryData={entryData}
      getEntryData={getEntryData}
      // Rapid entry
      rapidEntry={rapidEntry}
      rapidPatientUniqueId={rapidPatientUniqueId}
      setRapidPatientUniqueId={setRapidPatientUniqueId}
      rapidPatientFullName={rapidPatientFullName}
      setRapidPatientFullName={setRapidPatientFullName}
      selectedExistingPatientId={selectedExistingPatientId}
      setSelectedExistingPatientId={setSelectedExistingPatientId}
      setRapidEntryAction={setRapidEntryAction}
      // Field warnings.
      rapidPatientUniqueIdWarning={rapidPatientUniqueIdWarning}
      rapidPatientFullNameWarning={rapidPatientFullNameWarning}
    >
      <div className="flex flex-col items-center justify-center w-full h-full Xbg-red-500">
        {/* <h6>{JSON.stringify(comparisonItems)}</h6> */}
        {modalImage && (
          <ImageViewModal exitFunction={setModalImage} setValue={null}>
            <ImageViewer modalImage={modalImage} />
          </ImageViewModal>
        )}

        {entryBrowserModalOpen && (
          <EntryBrowserModal
            exitFunction={setEntryBrowserModalOpen}
            setValue={false}
          >
            <EntryBrowser
              patientId={patientId}
              comparisonItems={comparisonItems}
              setComparisonItems={setComparisonItems}
              comparisonItemLocation={comparisonItemLocation}
              setEntryBrowserModalOpen={setEntryBrowserModalOpen}
            />
          </EntryBrowserModal>
        )}

        {compareModalOpen && (
          <ComparisonModal
            comparisonItems={comparisonItems}
            setComparisonItems={setComparisonItems}
            currentComparisonIndex={currentComparisonIndex}
            exitFunction={setCompareModalOpen}
            setValue={false}
            currentMenu={currentMenu}
            setCurrentMenu={setCurrentMenu}
            touchControls={touchControls}
            setTouchControls={setTouchControls}
          >
            <ComparisonMenu
              setComparisonItems={setComparisonItems}
              comparisonItems={comparisonItems}
              currentComparisonIndex={currentComparisonIndex}
              currentMenu={currentMenu}
              touchControls={touchControls}
            />
          </ComparisonModal>
        )}

        {comparisonItems.map((comparisonItem, index) => {
          {
            /* This creates a unique key, and avoid resource strain that UUID crates. UUID regenerates with each dom change. */
          }
          const fileOneKey = comparisonItem?.fileOne[0]?.path
            ? comparisonItem.fileOne[0].path
            : `fileOne${index}None`;

          const fileTwoKey = comparisonItem?.fileTwo[0]?.path
            ? comparisonItem.fileTwo[0].path
            : `fileTwo${index}None`;

          const key = `${fileOneKey}-${fileTwoKey}`;

          return (
            <ComparisonCard
              key={key}
              entryType={"compare"}
              index={index}
              setCurrentComparisonIndex={setCurrentComparisonIndex}
              baseComparisonItem={baseComparisonItem}
              comparisonItems={comparisonItems}
              comparisonItem={comparisonItem}
              setComparisonItems={setComparisonItems}
              setCurrentMenu={setCurrentMenu}
              setModalImage={setModalImage}
              compareModalOpen={compareModalOpen}
              setCompareModalOpen={setCompareModalOpen}
              entryBrowserModalOpen={entryBrowserModalOpen}
              setEntryBrowserModalOpen={setEntryBrowserModalOpen}
              comparisonItemLocation={comparisonItemLocation}
              setComparisonItemLocation={setComparisonItemLocation}
              rapidEntry={rapidEntry}
            />
          );
        })}

        <NewPairButton
          comparisonItems={comparisonItems}
          pairLimit={pairLimit}
          setComparisonItems={setComparisonItems}
          baseComparisonItem={baseComparisonItem}
          newItemPrompt={"New Pair"}
        />
      </div>
    </EntryScreenTemplate>
  );
}

export default ComparisonEntryScreen;
